import React, { useState, useEffect } from 'react';
import { User as UserIcon, Mail, Phone, Lock, Shield, Save, Loader2, FileText, Trash2, Camera, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { AuthService } from '../../handling_data/services/AuthService';

import { LoginResponseDto, User } from '../../handling_data/dtos/AuthDto';

export const SettingsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState<User | null>(null);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contact_phone: '',
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });

    const [certificates, setCertificates] = useState<File[]>([]);
    const [existingCerts, setExistingCerts] = useState<string[]>([]);
    const [deletedCerts, setDeletedCerts] = useState<string[]>([]);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const latestUser = await AuthService.getProfile();
                if (latestUser) {
                    setUser(latestUser);
                    setFormData(prev => ({
                        ...prev,
                        name: latestUser.profile?.official_name || latestUser.name || '',
                        email: latestUser.email || '',
                        contact_phone: latestUser.profile?.contact_phone || ''
                    }));
                    setExistingCerts(latestUser.profile?.certificates || []);
                }
            } catch (error) {
                // Fallback to local storage if server fetch fails
                const currentUser = AuthService.getCurrentUser();
                if (currentUser) {
                    setUser(currentUser);
                    setFormData(prev => ({
                        ...prev,
                        name: currentUser.profile?.official_name || currentUser.name || '',
                        email: currentUser.email || '',
                        contact_phone: currentUser.profile?.contact_phone || ''
                    }));
                    setExistingCerts(currentUser.profile?.certificates || []);
                }
            }
        };

        fetchProfile();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setCertificates(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const removeNewCert = (index: number) => {
        setCertificates(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingCert = (path: string) => {
        setExistingCerts(prev => prev.filter(p => p !== path));
        setDeletedCerts(prev => [...prev, path]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.new_password && formData.new_password !== formData.new_password_confirmation) {
            alert(t('settings_page.passwords_dont_match'));
            return;
        }

        setLoading(true);
        try {
            const data = {
                ...formData,
                certificates,
                deleted_certificates: deletedCerts
            };
            const response = await AuthService.updateProfile(data);
            if (response.status === 'success') {
                alert(response.message || t('settings_page.update_success'));
                // Clear sensitive fields
                setFormData(prev => ({
                    ...prev,
                    current_password: '',
                    new_password: '',
                    new_password_confirmation: ''
                }));
                setCertificates([]);
                setDeletedCerts([]);
                // Reload user data from response
                setUser(response.data);
                setExistingCerts(response.data.profile?.certificates || []);
            } else {
                alert(response.message || t('settings_page.update_failed'));
            }
        } catch (error: any) {
            console.error('Update failed:', error);
            const msg = error.response?.data?.message || t('settings_page.update_error');
            alert(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-fade-in p-6">
            <div>
                <h2 className="text-3xl font-black text-gray-900 mb-2">{t('common.settings')}</h2>
                <p className="text-gray-500">{t('settings_page.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profile Section */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                <UserIcon className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">{t('settings_page.profile_section')}</h3>
                        </div>
                    </div>

                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 mx-1">{t('settings_page.full_name')}</label>
                            <div className="relative">
                                <UserIcon className={`absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`w-full ${i18n.language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                    placeholder={t('settings_page.full_name_placeholder')}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 mx-1">{t('settings_page.email')}</label>
                            <div className="relative">
                                <Mail className={`absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`w-full ${i18n.language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                    placeholder="email@example.com"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700 mx-1">{t('settings_page.phone')}</label>
                            <div className="relative">
                                <Phone className={`absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                                <input
                                    type="text"
                                    name="contact_phone"
                                    value={formData.contact_phone}
                                    onChange={handleChange}
                                    className={`w-full ${i18n.language === 'ar' ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'} py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 outline-none transition-all dir-ltr`}
                                    placeholder="+90 5XX XXX XXXX"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Password Section */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
                                <Shield className="w-6 h-6 text-orange-500" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">{t('settings_page.security_section')}</h3>
                        </div>
                    </div>

                    <div className="p-8 space-y-6">
                        {/* Certificates Management */}
                        <div className="space-y-4">
                            <label className="text-sm font-bold text-gray-700 mx-1">{t('settings_page.manage_certs')}</label>

                            {/* Existing Certs */}
                            {existingCerts.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                                    {existingCerts.map((cert, idx) => (
                                        <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                                            {cert.toLowerCase().endsWith('.pdf') ? (
                                                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2 p-2 text-center">
                                                    <FileText className="w-8 h-8" />
                                                    <span className="text-[10px] break-all">PDF Document</span>
                                                </div>
                                            ) : (
                                                <img src={`https://api.istanbulviillas.com/storage/${cert}`} className="w-full h-full object-cover" alt="" />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeExistingCert(cert)}
                                                className={`absolute top-2 ${i18n.language === 'ar' ? 'left-2' : 'right-2'} p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* New Certs */}
                            <div className="flex flex-wrap gap-4">
                                {certificates.map((file, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-primary/5 text-primary px-3 py-1.5 rounded-lg border border-primary/10 text-xs font-medium">
                                        <span className="max-w-[150px] truncate">{file.name}</span>
                                        <button type="button" onClick={() => removeNewCert(idx)}>
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                <label className="cursor-pointer flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-600 px-4 py-2 rounded-xl border border-dashed border-gray-300 transition-all text-sm font-bold">
                                    <Camera className="w-4 h-4" />
                                    <span>{t('settings_page.add_files')}</span>
                                    <input type="file" multiple className="hidden" onChange={handleFileChange} accept=".pdf,.jpg,.png" />
                                </label>
                            </div>
                        </div>

                        <div className="space-y-2 max-w-md">
                            <label className="text-sm font-bold text-gray-700 mx-1 text-red-600">{t('settings_page.current_password_label')}</label>
                            <div className="relative">
                                <Lock className={`absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                                <input
                                    type="password"
                                    name="current_password"
                                    value={formData.current_password}
                                    onChange={handleChange}
                                    required
                                    className={`w-full ${i18n.language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 rounded-xl border-2 border-red-50 focus:ring-2 focus:ring-red-500/20 outline-none transition-all`}
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-50">
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700 mx-1">{t('settings_page.new_password_label')}</label>
                                <div className="relative">
                                    <Lock className={`absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                                    <input
                                        type="password"
                                        name="new_password"
                                        value={formData.new_password}
                                        onChange={handleChange}
                                        className={`w-full ${i18n.language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700 mx-1">{t('settings_page.confirm_password_label')}</label>
                                <div className="relative">
                                    <Lock className={`absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                                    <input
                                        type="password"
                                        name="new_password_confirmation"
                                        value={formData.new_password_confirmation}
                                        onChange={handleChange}
                                        className={`w-full ${i18n.language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`flex ${i18n.language === 'ar' ? 'justify-start' : 'justify-end'} pt-4`}>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="bg-primary text-white px-10 py-4 rounded-2xl font-bold flex items-center gap-2 hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        <span>{t('settings_page.save_changes')}</span>
                    </Button>
                </div>
            </form>
        </div>
    );
};
